// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import {
  ActionType,
  IAction,
  IActionTypeLoginResult,
  IActionControllerTrendInfo,
  IActionControllerTrendData,
  IActionChangeInput,
  IActionChangeOutput,
  IActionCommand,
  IActionTypeSignUpResult,
  IActionTypeChangeUser,
  IActionCloudTrendData,
} from "actions";
import { Command, ImportFile } from "graphql/api";
import { ILogInfo } from "comtypes";
import { AppState, IStateScheduler, UserState } from "states/schedulerstates";
import { IState } from "states/states";
import { DataState, MainPanel } from "types";
import { isSet } from "utils";
import { NUM_OPC_TREND_ITEMS } from "sagas/apisagas";
import { isLocalhost } from "guiutils";
import { FileUploadStatus } from "dbtypes";

// uses webpack definitions
declare const IS_CLOUD: boolean;

function forceReload(state: IStateScheduler) {
  // force immediate reloading of data
  state.allData = {};
  state.alarm = {};
  state.parameter = {};
  state.ocppChargerList = {};
  state.ocppConnectorData = {};
  state.ocppRfidListBasic = {};
  state.ocppRfidListExt = {};
  state.ocppTariffList = {};
  state.pendingOcppSessionState = {};
  state.trendLoading = {};
}

export default function schedulerReducer(
  state: IStateScheduler,
  action: IAction<any>,
  rootState: IState,
): IStateScheduler {
  switch (action.type) {
    case ActionType.ACT_APP_LOADED:
      if (IS_CLOUD || !isLocalhost()) {
        state.appState = AppState.LOGGED_OUT;
      } else {
        // on local embyt controller display we skip some states
        state.appState = AppState.SITE_SELECTED;
      }
      return state;

    case ActionType.ACT_VISIBILITY_CHANGE: {
      const isVisible = action.payload as boolean;
      state.isInBackground = !isVisible;
      return state;
    }

    case ActionType.ACT_GOTO_LOGIN:
      state.appState = AppState.LOGGED_OUT;
      return state;

    case ActionType.ACT_SIGNUP:
      state.userState = UserState.SINGING_UP;
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_SIGNUP_CONFIRM:
      state.userState = UserState.SINGING_UP_CONFIRM;
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_SIGNUP_REQ:
    case ActionType.ACT_SIGNUP_CONFIRM_REQ:
    case ActionType.ACT_SIGNUP_RESEND_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;

    case ActionType.ACT_SIGNUP_SUCC: {
      // we've got success answer from last call -> check in which state we are...
      const payload = action.payload as IActionTypeSignUpResult;
      state.loginDataState = DataState.SAVE_SUCC;

      if (state.userState === UserState.SINGING_UP) {
        // ok -- we've sent our data and we were successfully registered -> now waiting for
        // confirmation....
        state.userState = UserState.SINGING_UP_CONFIRM;
      }
      return state;
    }

    case ActionType.ACT_LOGIN_REQ:
      state.login.lastRequest = Date.now();
      state.loginDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_LOGIN_SUCC: {
      const payload = action.payload as IActionTypeLoginResult;
      state.login.lastData = Date.now();
      state.loginDataState = DataState.SAVE_SUCC;
      state.userState =
        IS_CLOUD && !payload.isRfidUser && !isSet(payload.email)
          ? UserState.CHANGING_PASSWORD
          : UserState.UNKNOWN;
      if (!IS_CLOUD) {
        // embyt or LINX HW
        if (isLocalhost()) {
          // local login is optional
          state.appState = AppState.READY;
        } else {
          // no need to load site and user list and to select site
          state.appState = AppState.SITE_SELECTED;
        }
      } else {
        if (state.userState === UserState.UNKNOWN) {
          state.appState = AppState.LOGGED_IN;
        }
      }
      return state;
    }
    case ActionType.ACT_LOGIN_FAIL:
      state.userState = UserState.UNKNOWN;
      state.loginDataState = DataState.SAVE_FAIL;
      state.appState = AppState.LOGGED_OUT;
      return state;
    case ActionType.ACT_LOGIN_FAIL_AWS:
      state.userState = UserState.UNKNOWN;
      state.loginDataState = DataState.SAVE_FAIL;
      state.appState = AppState.LOGGED_OUT;
      return state;

    case ActionType.ACT_LOGOUT_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_LOGOUT_SUCC:
      state.appState = AppState.LOGGED_OUT;
      state.loginDataState = DataState.SAVE_SUCC;
      state.siteList = {};
      state.userState = UserState.UNKNOWN;
      state.userListBasic = {};
      // ensure reloading of site data
      forceReload(state);
      return state;
    case ActionType.ACT_LOGOUT_FAIL:
      state.loginDataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_FORGOT_PASSWORD:
      state.userState = UserState.FORGOT_PASSWORD;
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_BACK_TO_LOGIN:
      state.userState = UserState.UNKNOWN;
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_FORGOT_PASSWORD_CODE_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_FORGOT_PASSWORD_CODE_SUCC:
      state.loginDataState = DataState.SAVE_SUCC;
      state.userState = UserState.PASSWORD_CONFIRM;
      return state;

    case ActionType.ACT_FORGOT_PASSWORD_CHANGE_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_FORGOT_PASSWORD_CHANGE_SUCC:
      state.loginDataState = DataState.SAVE_SUCC;
      state.userState = UserState.PASSWORD_CHANGED;
      // maybe we should show some info on the LogIn screen, that we have changed the Password
      // and that the user should try to login again...
      return state;

    case ActionType.ACT_COGNITO_FAIL:
      state.loginDataState = DataState.SAVE_FAIL;
      return state;
    case ActionType.ACT_COGNITO_FAIL_AWS:
      state.loginDataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_FORCE_PWD_CHANGE:
      state.userState = UserState.CHANGING_PASSWORD;
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_CANCEL_PWD_CHANGE:
      state.userState = UserState.UNKNOWN;
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_CHANGE_PASSWORD:
      state.userState = UserState.CHANGING_PASSWORD;
      return state;
    case ActionType.ACT_CHANGE_AWSUSER_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_AWSUSER_SUCC:
      state.loginDataState = DataState.SAVE_SUCC;
      state.userState = UserState.UNKNOWN;
      return state;
    case ActionType.ACT_CHANGE_AWSUSER_FAIL:
      state.loginDataState = DataState.SAVE_FAIL;
      // leave user state as is!
      return state;

    case ActionType.ACT_CHANGE_USER_REQ: {
      const userId = (action.payload as IActionTypeChangeUser).id;
      state.userState = isSet(userId) ? UserState.UNKNOWN : UserState.CREATE_NEW_USER;
      state.saveState = DataState.SAVE_REQ;
      return state;
    }
    case ActionType.ACT_CHANGE_USER_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_CHANGE_USER_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_CHANGE_SITESETTINGS_REQ:
      state.saveState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_SITESETTINGS_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_CHANGE_SITESETTINGS_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_GET_SITES_REQ:
      state.siteList.lastRequest = Date.now();
      // do not (re)set parameter state here to keep error message present
      return state;

    case ActionType.ACT_GET_SITES_SUCC:
      state.siteList.lastData = Date.now();
      // switch state if we have both, site and user lists
      if (isSet(state.userListBasic.lastData) && state.appState < AppState.SITE_LIST_LOADED) {
        state.appState = AppState.SITE_LIST_LOADED;
      }
      return state;
    case ActionType.ACT_GET_USERLIST_BASIC_REQ:
      state.userListBasic.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_USERLIST_BASIC_SUCC:
      state.userListBasic.lastData = Date.now();
      // when we receive basic data, extended data gets lost
      state.userListExt.lastData = undefined;
      // except for RFID users, where we do not need the site list and we switch directly to site-selected
      if (rootState.site.user?.isRfidUser && state.appState < AppState.SITE_SELECTED) {
        state.appState = AppState.SITE_SELECTED;
      }
      // for non-rfid users, we switch state if we have both, site and user lists
      // except for RFID users, where we do not need the site list
      else if (isSet(state.siteList.lastData) && state.appState < AppState.SITE_LIST_LOADED) {
        state.appState = AppState.SITE_LIST_LOADED;
      }
      return state;
    case ActionType.ACT_GET_USERLIST_EXT_REQ:
      state.userListExt.lastRequest = Date.now();
      // we need to clear lastData to avoid indicating data ready although another user was loaded before
      state.userListExt.lastData = undefined;
      return state;
    case ActionType.ACT_GET_USERLIST_EXT_SUCC:
      state.userListExt.lastData = Date.now();
      return state;

    case ActionType.ACT_SELECT_SITE:
      state.appState = AppState.SITE_SELECTED;
      state.parameterDataState = DataState.DIRTY;
      // allow immediate reloading of parameter data
      forceReload(state);
      return state;

    case ActionType.ACT_DISCONNECT_SITE:
      if (state.appState >= AppState.SITE_SELECTED) {
        state.appState = AppState.SITE_LIST_LOADED;
      }
      forceReload(state);
      return state;

    case ActionType.ACT_LIVE_DATA_SUBSCRIBED:
      state.appState = AppState.LIVE_DATA_SUBSCRIBED;
      return state;

    case ActionType.ACT_GET_PARAMETER_REQ:
      state.parameter.lastRequest = Date.now();
      // do not (re)set parameter state here to keep error message present
      return state;
    case ActionType.ACT_GET_PARAMETER_SUCC:
      state.parameter.lastData = Date.now();
      state.parameterDataState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_GET_PARAMETER_FAIL:
      state.parameterDataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_GET_OCPP_CHARGERS_REQ:
      state.ocppChargerList.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_CHARGERS_SUCC:
      state.ocppChargerList.lastData = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_CONNECTORS_REQ:
      state.ocppConnectorData.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_CONNECTORS_SUCC:
      state.ocppConnectorData.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_OCPP_RFIDS_BASIC_REQ:
      state.ocppRfidListBasic.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_RFIDS_BASIC_SUCC:
      state.ocppRfidListBasic.lastData = Date.now();
      // when we receive basic RFID data, extended data gets lost
      state.ocppRfidListExt.lastData = undefined;
      return state;
    case ActionType.ACT_GET_OCPP_RFIDS_EXT_REQ:
      state.ocppRfidListExt.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_RFIDS_EXT_SUCC:
      state.ocppRfidListExt.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_OCPP_SITE_TARIFFS_REQ:
      state.ocppTariffList.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_SITE_TARIFFS_SUCC:
      state.ocppTariffList.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_ALARMS_REQ:
      state.alarm.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_ALARMS_SUCC:
      state.alarm.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_CONTROLLERDATA_REQ:
      state.controllerData.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_CONTROLLERDATA_SUCC:
      state.controllerData.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_ALLDATA_REQ:
      state.allData.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_ALLDATA_SUCC:
      state.allData.lastData = Date.now();
      return state;

    case ActionType.ACT_REGISTER_FOR_PUSH_DATA_REQ:
      // in cloud, registering for live data equals a request for it
      state.allData.lastRequest = Date.now();
      return state;
    case ActionType.ACT_REGISTER_FOR_PUSH_DATA_SUCC:
      state.isOffline = false;
      return state;
    case ActionType.ACT_REGISTER_FOR_PUSH_DATA_FAIL:
      state.isOffline = true;
      return state;
    case ActionType.ACT_REGISTER_FOR_PUSH_DATA_TIMEOUT:
      // we have been offline for long time, so we need to reload all data
      forceReload(state);
      return state;

    case ActionType.ACT_PARAMETER_LOADED:
      if (state.appState < AppState.PARAMETER_LOADED) {
        state.appState = AppState.PARAMETER_LOADED;
      }
      return state;

    case ActionType.ACT_INIT_DONE:
      state.appState = AppState.READY;
      // in cloud we can immediately start trend loading and do not need to wait for trend info
      // no trend loading if we face a site without parameters, i.e. a virtual ocpp charger site
      if (IS_CLOUD && isSet(state.parameter.lastData)) {
        state.trendLoadingState = DataState.SAVE_REQ;
      }
      return state;

    case ActionType.ACT_NAVIGATION: {
      if (
        rootState.router.panel !== MainPanel.USER_SETTINGS ||
        state.userState !== UserState.CREATE_NEW_USER
      ) {
        // only clear the saveState on any other panel than USER_SETTINGS
        // or we did not create a new user!
        state.saveState = DataState.CLEAN;
      }

      // we abort a password change request
      state.userState = UserState.UNKNOWN;

      return state;
    }

    case ActionType.ACT_SET_PAGE_DIRTY:
      if (
        (!isSet(action.payload) || action.payload === true) &&
        state.saveState !== DataState.SAVE_REQ
      ) {
        state.saveState = DataState.DIRTY;
      }
      return state;

    case ActionType.ACT_MODAL_CANCEL:
      // we reset save status
      if (rootState.router.panel !== MainPanel.SITES) {
        state.saveState = DataState.CLEAN;
      }
      return state;

    case ActionType.ACT_GET_TRENDINFO_SUCC:
      // trendinfo in cloud just gets data boundaries and has no influence on trend loading
      if (!IS_CLOUD) {
        // engine
        const payload = action.payload as IActionControllerTrendInfo;
        state.trendLoadingState = payload.itemCnt.some((curCnt) => curCnt > 0)
          ? DataState.SAVE_REQ
          : DataState.CLEAN;
      }
      return state;
    case ActionType.ACT_GET_TREND_REQ:
      state.trendLoading.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_CONTROLLERTREND_SUCC: {
      state.trendLoading.lastData = Date.now();
      // clear lastRequest to load next trend data even before its timeout
      state.trendLoading.lastRequest = undefined;
      const payload = action.payload as IActionControllerTrendData;
      // as long as we receive data, we expect to get more
      state.trendLoadingState =
        payload.data.length === NUM_OPC_TREND_ITEMS ? DataState.SAVE_REQ : DataState.SAVE_SUCC;
      return state;
    }
    case ActionType.ACT_GET_CLOUDTREND_SUCC: {
      const payload = action.payload as IActionCloudTrendData;
      state.trendLoadingProgress = payload.progress;
      if (payload.progress === 1) {
        // finished now!
        state.trendLoading.lastData = Date.now();
        state.allData.lastRequest = undefined;
        state.trendLoadingState = DataState.SAVE_SUCC;
      }
      return state;
    }
    case ActionType.ACT_GET_CHARGERTREND_SUCC:
      state.trendLoading.lastData = Date.now();
      state.chargingSession.lastData = Date.now();
      // we get all data in a single request
      state.trendLoadingState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_APPEND_CLOUDTREND:
      state.trendLoading.lastData = Date.now();
      return state;
    case ActionType.ACT_CHANGE_TREND_SUCC:
      state.trendLoading.lastData = Date.now();
      return state;
    case ActionType.ACT_GET_TREND_FAIL:
      // we stop trend loading on such an event to avoid endless loops
      state.trendLoadingState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_GET_IOTREND_REQ:
      state.ioTrendLoading.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_CLOUDIOTREND_SUCC:
      state.ioTrendLoading.lastData = Date.now();
      // we get all data in a single request
      state.isIoTrendLoading = false;
      return state;
    case ActionType.ACT_GET_CLOUDIOTREND_FULL_SUCC:
      state.ioTrendLoading.lastData = Date.now();
      return state;
    case ActionType.ACT_GET_CONTROLLERIOTREND_SUCC:
      state.ioTrendLoading.lastData = Date.now();
      state.allData.lastRequest = undefined;
      // as long as we receive data, we expect to get more
      // this will not work as we load 2 trends here; however, we will mostly just get one answer anyway
      state.isIoTrendLoading = action.payload.data.length === NUM_OPC_TREND_ITEMS;
      return state;
    case ActionType.ACT_GET_IOTREND_FAIL:
      // we stop trend loading on such an event to avoid endless loops
      state.isIoTrendLoading = false;
      return state;

    case ActionType.ACT_UPDATE_TREND:
      state.trendLoadingState = DataState.SAVE_REQ;
      state.trendLoadingProgress = 0;
      // ensure to invoke new request immediately
      state.trendLoading.lastRequest = undefined;
      return state;
    case ActionType.ACT_UPDATE_IOTREND:
      state.isIoTrendLoading = true;
      // ensure to invoke new request immediately
      state.ioTrendLoading.lastRequest = undefined;
      return state;

    case ActionType.ACT_CHANGE_INPUT_REQ: {
      const payload = action.payload as IActionChangeInput;
      state.pendingCommand = {
        timestampMs: Date.now(),
        seqNr: payload.commandSeqNr,
        command: Command.setInput,
        cmdArgs: payload,
      };
      return state;
    }
    case ActionType.ACT_CHANGE_INPUT_FAIL:
      state.pendingCommand = undefined; // clear pending command
      return state;
    case ActionType.ACT_CHANGE_INPUT_SUCC:
      state.pendingCommand = undefined; // clear pending command
      return state;

    case ActionType.ACT_CHANGE_OUTPUT_REQ: {
      const payload = action.payload as IActionChangeOutput;
      state.pendingCommand = {
        timestampMs: Date.now(),
        seqNr: payload.commandSeqNr,
        command: Command.setOutput,
        cmdArgs: payload,
      };
      return state;
    }
    case ActionType.ACT_CHANGE_OUTPUT_FAIL:
      state.pendingCommand = undefined; // clear pending command
      return state;
    case ActionType.ACT_CHANGE_OUTPUT_SUCC:
      state.pendingCommand = undefined; // clear pending command
      state.setOutput.lastRequest = Date.now();
      return state;

    case ActionType.ACT_OPEN_SSH_TUNNEL_REQ: {
      const payload = action.payload as IActionCommand;
      state.pendingCommand = {
        timestampMs: Date.now(),
        seqNr: payload.commandSeqNr,
        command: Command.openSshTunnel,
        cmdArgs: null,
      };
      // clear previous fail flag, but do not reset data-changed flag
      if (state.saveState === DataState.SAVE_FAIL) {
        state.saveState = DataState.EMPTY;
      }
      return state;
    }
    case ActionType.ACT_OPEN_SSH_TUNNEL_SUCC:
      state.pendingCommand = undefined; // clear pending command
      return state;
    case ActionType.ACT_OPEN_SSH_TUNNEL_FAIL:
      state.pendingCommand = undefined; // clear pending command
      state.saveState = DataState.SAVE_FAIL; // indicate failure, even if save state is not used for cmd
      return state;

    case ActionType.ACT_REBOOT_REQ: {
      const payload = action.payload as IActionCommand;
      state.pendingCommand = {
        timestampMs: Date.now(),
        seqNr: payload.commandSeqNr,
        command: Command.rebootController,
        cmdArgs: null,
      };
      // clear previous fail flag, but do not reset data-changed flag
      if (state.saveState === DataState.SAVE_FAIL) {
        state.saveState = DataState.EMPTY;
      }
      return state;
    }
    case ActionType.ACT_REBOOT_SUCC:
      state.pendingCommand = undefined; // clear pending command
      return state;
    case ActionType.ACT_REBOOT_FAIL:
      state.pendingCommand = undefined; // clear pending command
      state.saveState = DataState.SAVE_FAIL; // indicate failure, even if save state is not used for cmd
      return state;

    case ActionType.ACT_GET_PENDING_OCPP_SESSIONS_REQ:
      state.pendingOcppSessionState.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_PENDING_OCPP_SESSIONS_SUCC:
      state.pendingOcppSessionState.lastData = Date.now();
      return state;
    case ActionType.ACT_NEW_CHARGING_SESSION_DATA:
      // live/push data for new session info should also update
      state.pendingOcppSessionState.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_OCPP_SESSIONS_REQ:
      state.ocppSessionState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_GET_OCPP_SESSIONS_SUCC:
      state.ocppSessionState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_GET_OCPP_SESSIONS_FAIL:
      state.ocppSessionState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_SELECT_CHARGING_SESSION:
      state.chargingPower.lastRequest = Date.now();
      state.chargingSession.lastRequest = Date.now();
      return state;

    case ActionType.ACT_EDIT_REPORT: // intentional fall-through
    case ActionType.ACT_EDIT_TRENDMONITOR:
      state.saveState = DataState.CLEAN;
      return state;
    case ActionType.ACT_SAVE_REPORT_REQ:
    case ActionType.ACT_SAVE_REPORTS_SETTINGS_REQ:
      state.saveState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_REPORT_SETTINGS_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;
    case ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_REQ:
      state.saveState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_GET_CHARGER_POWER_SUCC:
      state.chargingPower.lastData = Date.now();
      break;
    case ActionType.ACT_SAVE_CHARGER_POWER_REQ:
      state.saveState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_CHARGER_POWER_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_SAVE_CHARGER_POWER_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_PERFORM_FILE_UPLOAD_SUCC:
      state.isFileUploadLoading = true;
      return state;
    case ActionType.ACT_GET_FILEUPLOADS_REQ:
      state.fileUploadLoading.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_FILEUPLOADS_SUCC: {
      const payload = [...(action.payload as ImportFile[])];

      const lastUpload =
        payload.length > 0 ? payload.sort((a, b) => a.addedOn - b.addedOn).pop() : undefined;

      state.isFileUploadLoading =
        isSet(lastUpload) &&
        [FileUploadStatus.INITIAL, FileUploadStatus.RECEIVED].includes(
          lastUpload.status as FileUploadStatus,
        );

      return state;
    }
  }
  return state;
}
